import {
  createBankTitleMap,
  summarizeProductBanks,
  countsTowardsBankLimit,
  getProductBankLimitState,
  getProjectCompanyCodeStatus,
  getProjectCompanyCodeValues,
  flattenProductBankAssignments,
  getProductBankValidationMessage,
  validateProductBankConfiguration,
} from 'utils';
import {
  Box,
  Chip,
  Card,
  alpha,
  Alert,
  Button,
  Select,
  Divider,
  Tooltip,
  useTheme,
  MenuItem,
  Typography,
  IconButton,
  InputLabel,
  DialogTitle,
  FormControl,
  CardContent,
  DialogActions,
  DialogContent,
  Grid2 as Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ResponsiveDialog from 'components/responsive-dialog';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ProductCompanyStatusChip from 'components/product-company-status-chip';
import BankStatusChip, { getBankStatusVisual } from 'components/bank-status-chip';
import { ProjectCompanyCode, ProjectCompanyCodeBank, ProjectBankItem, UsedBank } from 'library/types';
import { BOOK_BANK_STATUS_TYPES, BOOK_BANK_STATUS_TYPE_ENUM, getBookBankStatusTypeTranslated } from 'enums';

type ProductBanksDialogProps = {
  open: boolean;
  onClose: () => void;
  banks: ProjectCompanyCodeBank[];
  bankCount: number;
  availableCompanyCodes: ProjectCompanyCode[];
  usedBanks: UsedBank[];
  editable?: boolean;
  productTitle?: string;
  onSave?: (updatedBanks: ProjectCompanyCodeBank[]) => void;
};

const ProductBanksDialog = ({
  open,
  onClose,
  banks = [],
  bankCount = 0,
  availableCompanyCodes = [],
  usedBanks = [],
  editable = false,
  productTitle,
  onSave,
}: ProductBanksDialogProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Local draft state when editing
  const [selectedBankId, setSelectedBankId] = useState<number | ''>('');
  const [draftBanks, setDraftBanks] = useState<ProjectCompanyCodeBank[]>([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<BOOK_BANK_STATUS_TYPE_ENUM>(BOOK_BANK_STATUS_TYPE_ENUM.NONE);
  const availableCompanyCodeValues = useMemo(
    () => getProjectCompanyCodeValues(availableCompanyCodes),
    [availableCompanyCodes]
  );

  useEffect(() => {
    if (open) {
      // Deep clone banks for editing
      const cloned = banks.map((group) => ({
        companyCode: group.companyCode,
        banks: (group.banks || []).map((b) => ({ ...b })),
      }));
      setDraftBanks(cloned);
      setSelectedCompanyCode(availableCompanyCodeValues[0] ?? '');
      setSelectedBankId('');
      setSelectedStatus(BOOK_BANK_STATUS_TYPE_ENUM.NONE);
    }
  }, [open, banks, availableCompanyCodeValues]);

  const activeBanksList = editable ? draftBanks : banks;

  const bankSummary = useMemo(() => summarizeProductBanks(activeBanksList), [activeBanksList]);
  const totalCurrentBanks = bankSummary.assignmentCount;
  const bankLimitUsageCount = bankSummary.bankLimitUsageCount;

  const bankLimitState = getProductBankLimitState(bankCount, bankLimitUsageCount);
  const isLimitReached = bankLimitState === 'reached';
  const nextBankLimitUsageCount = bankLimitUsageCount + (countsTowardsBankLimit(selectedStatus) ? 1 : 0);
  const nextBankLimitState = getProductBankLimitState(bankCount, nextBankLimitUsageCount);
  const cannotAddBank = nextBankLimitState === 'invalid' || nextBankLimitState === 'exceeded';
  const hasNoCompanyCodes = availableCompanyCodeValues.length === 0;
  const resolvedProductTitle =
    productTitle ?? intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' });
  const bankLimitUsageDescription = intl.formatMessage(
    {
      id: 'project.table.bankLimitUsage',
      defaultMessage: 'Banka limiti: {current} / {limit} pasif olmayan banka · Toplam {total} atama',
    },
    { current: bankLimitUsageCount, limit: bankCount, total: totalCurrentBanks }
  );
  const draftValidationIssue = useMemo(
    () =>
      validateProductBankConfiguration({
        id: 0,
        companyCodes: availableCompanyCodes,
        bankCount,
        banks: draftBanks,
      }),
    [availableCompanyCodes, bankCount, draftBanks]
  );
  const draftValidationMessage = draftValidationIssue
    ? getProductBankValidationMessage(draftValidationIssue, resolvedProductTitle, intl)
    : null;
  // Flattened view for rendering
  const bankTitleMap = useMemo(() => createBankTitleMap(usedBanks), [usedBanks]);
  const flattenedBanks = useMemo(
    () =>
      flattenProductBankAssignments(activeBanksList).map((assignment) => ({
        ...assignment,
        bankTitle:
          bankTitleMap.get(assignment.bankId) ??
          `${intl.formatMessage({ id: 'project.table.bank', defaultMessage: 'Banka' })} #${assignment.bankId}`,
      })),
    [activeBanksList, bankTitleMap, intl]
  );

  const handleAddBank = () => {
    if (hasNoCompanyCodes) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'project.table.noCompanyCodesSelectedForProduct',
          defaultMessage:
            'Bu ürüne henüz şirket kodu seçilmemiş. Banka ekleyebilmek için lütfen önce bu ürün için şirket kodu seçiniz.',
        }),
        { variant: 'warning' }
      );
      return;
    }

    if (!selectedCompanyCode) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'project.table.companyCodeRequired',
          defaultMessage: 'Şirket kodu seçilmelidir!',
        }),
        { variant: 'warning' }
      );
      return;
    }

    if (selectedBankId === '' || selectedBankId === 0) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'project.table.bankRequired',
          defaultMessage: 'Banka seçilmelidir!',
        }),
        { variant: 'warning' }
      );
      return;
    }

    if (bankLimitState === 'invalid') {
      enqueueSnackbar(getProductBankValidationMessage({ code: 'invalid-bank-limit' }, resolvedProductTitle, intl), {
        variant: 'warning',
      });
      return;
    }

    if (nextBankLimitState === 'exceeded') {
      enqueueSnackbar(
        intl.formatMessage(
          {
            id: 'project.table.bankCountLimitExceeded',
            defaultMessage: 'Pasif olmayan banka sayısı ({current}), banka limitini ({max}) aşamaz!',
          },
          { current: nextBankLimitUsageCount, max: bankCount }
        ),
        { variant: 'warning' }
      );
      return;
    }

    // Check duplicate
    const existingGroup = draftBanks.find((g) => g.companyCode === selectedCompanyCode);
    const isDuplicateBank = draftBanks.some(
      (group) => group.companyCode === selectedCompanyCode && group.banks.some((bank) => bank.bankId === selectedBankId)
    );

    if (isDuplicateBank) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'project.table.duplicateBank',
          defaultMessage: 'Bu şirket için bu banka zaten eklenmiş!',
        }),
        { variant: 'warning' }
      );
      return;
    }

    const newBankItem: ProjectBankItem = {
      bankId: Number(selectedBankId),
      status: selectedStatus,
    };

    if (existingGroup) {
      setDraftBanks((prev) =>
        prev.map((g) => (g.companyCode === selectedCompanyCode ? { ...g, banks: [...g.banks, newBankItem] } : g))
      );
    } else {
      setDraftBanks((prev) => [
        ...prev,
        {
          companyCode: selectedCompanyCode,
          banks: [newBankItem],
        },
      ]);
    }

    setSelectedBankId('');
    setSelectedStatus(BOOK_BANK_STATUS_TYPE_ENUM.NONE);
  };

  const handleDeleteBank = (companyCode: string, bankId: number) => {
    setDraftBanks((prev) =>
      prev
        .map((g) => {
          if (g.companyCode !== companyCode) return g;
          return {
            ...g,
            banks: g.banks.filter((b) => b.bankId !== bankId),
          };
        })
        .filter((g) => g.banks.length > 0)
    );
  };

  const handleStatusChange = (companyCode: string, bankId: number, newStatus: BOOK_BANK_STATUS_TYPE_ENUM) => {
    setDraftBanks((prev) =>
      prev.map((g) => {
        if (g.companyCode !== companyCode) return g;
        return {
          ...g,
          banks: g.banks.map((b) => (b.bankId === bankId ? { ...b, status: newStatus } : b)),
        };
      })
    );
  };

  const handleCommitSave = () => {
    if (draftValidationIssue) {
      enqueueSnackbar(getProductBankValidationMessage(draftValidationIssue, resolvedProductTitle, intl), {
        variant: 'warning',
      });
      return;
    }

    if (onSave) {
      onSave(draftBanks);
    }
    onClose();
  };

  const statusOptions = BOOK_BANK_STATUS_TYPES.map((status) => ({
    value: status,
    label: getBookBankStatusTypeTranslated(status),
  }));

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1.5,
        }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}>
            <AccountBalanceIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              <FormattedMessage id="project.table.banksModalTitle" defaultMessage="Ürün Bankaları" />
            </Typography>
            {productTitle && (
              <Typography variant="caption" color="text.secondary">
                {productTitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          {/* Limit Badge */}
          <Tooltip title={bankLimitUsageDescription}>
            <Chip
              label={`${bankLimitUsageCount} / ${bankCount} ${intl.formatMessage({ id: 'dashboard.banks', defaultMessage: 'Banka' })}`}
              aria-label={bankLimitUsageDescription}
              color={
                bankLimitState === 'invalid' || bankLimitState === 'exceeded'
                  ? 'error'
                  : isLimitReached
                    ? 'warning'
                    : bankLimitUsageCount > 0
                      ? 'primary'
                      : 'default'
              }
              variant="filled"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          </Tooltip>

          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          pt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          '& > *': { flexShrink: 0 },
        }}>
        {/* Editable Add Form */}
        {editable && (
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.05 : 0.02),
              borderColor: alpha(theme.palette.primary.main, 0.2),
            }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="primary.main">
              <FormattedMessage id="project.table.addBank" defaultMessage="Banka Ekle" />
            </Typography>

            {hasNoCompanyCodes && (
              <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
                <FormattedMessage
                  id="project.table.noCompanyCodesSelectedForProduct"
                  defaultMessage="Bu ürüne henüz şirket kodu seçilmemiş. Banka ekleyebilmek için lütfen önce bu ürün için şirket kodu seçiniz."
                />
              </Alert>
            )}

            {draftValidationMessage ? (
              <Alert severity="error" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
                {draftValidationMessage}
              </Alert>
            ) : isLimitReached ? (
              <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
                <FormattedMessage
                  id="project.table.bankCountLimitReached"
                  defaultMessage="Pasif olmayan banka limitine ulaşıldı ({current} / {max}). Pasif banka ekleyebilirsiniz. Pasif olmayan yeni bir banka için mevcut bir bankayı pasife alın veya limiti artırın."
                  values={{ current: bankLimitUsageCount, max: bankCount }}
                />
              </Alert>
            ) : null}

            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 12, sm: 3.5 }}>
                <FormControl fullWidth size="small" disabled={hasNoCompanyCodes}>
                  <InputLabel id="select-company-code-label">
                    <FormattedMessage id="project.table.code" defaultMessage="Şirket Kodu" />
                  </InputLabel>
                  <Select
                    labelId="select-company-code-label"
                    value={selectedCompanyCode}
                    label={intl.formatMessage({ id: 'project.table.code', defaultMessage: 'Şirket Kodu' })}
                    onChange={(e) => setSelectedCompanyCode(e.target.value)}>
                    {availableCompanyCodes.map(({ companyCode, status }) => (
                      <MenuItem key={companyCode} value={companyCode}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
                          <Typography variant="body2">{companyCode}</Typography>
                          <ProductCompanyStatusChip status={status} sx={{ height: 20, fontSize: '0.68rem' }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small" disabled={hasNoCompanyCodes}>
                  <InputLabel id="select-bank-label">
                    <FormattedMessage id="sidebar.usedBanks" defaultMessage="Banka" />
                  </InputLabel>
                  <Select
                    labelId="select-bank-label"
                    value={selectedBankId}
                    label={intl.formatMessage({ id: 'sidebar.usedBanks', defaultMessage: 'Banka' })}
                    onChange={(e) => setSelectedBankId(Number(e.target.value))}>
                    {usedBanks.map((ub) => (
                      <MenuItem key={ub.id} value={ub.id}>
                        {ub.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small" disabled={hasNoCompanyCodes}>
                  <InputLabel id="select-status-label">
                    <FormattedMessage id="project.table.bankStatus" defaultMessage="Durum" />
                  </InputLabel>
                  <Select
                    labelId="select-status-label"
                    value={selectedStatus}
                    label={intl.formatMessage({ id: 'project.table.bankStatus', defaultMessage: 'Durum' })}
                    onChange={(e) => setSelectedStatus(Number(e.target.value) as BOOK_BANK_STATUS_TYPE_ENUM)}>
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  disabled={cannotAddBank || hasNoCompanyCodes || !selectedCompanyCode || !selectedBankId}
                  onClick={handleAddBank}
                  sx={{
                    height: 40,
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    '&.Mui-disabled': {
                      color: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)',
                      backgroundColor:
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      boxShadow: 'none',
                      borderColor: 'transparent',
                    },
                  }}>
                  <FormattedMessage id="project.table.addRow" defaultMessage="Ekle" />
                </Button>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Bank List View */}
        {flattenedBanks.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography color="text.secondary">
              <FormattedMessage id="project.table.noBanks" defaultMessage="Henüz banka eklenmemiş" />
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {flattenedBanks.map((item, idx) => {
              const statusCfg = getBankStatusVisual(item.status, theme);
              const companyStatus = getProjectCompanyCodeStatus(availableCompanyCodes, item.companyCode);

              return (
                <Grid size={{ xs: 12, sm: 6 }} key={`${item.companyCode}-${item.bankId}-${idx}`}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: statusCfg.color,
                        boxShadow: theme.shadows[1],
                      },
                    }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                            {item.bankTitle}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                          <Chip
                            label={item.companyCode}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 22,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                            }}
                          />
                          {companyStatus !== undefined && (
                            <ProductCompanyStatusChip
                              status={companyStatus}
                              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                        {editable ? (
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={item.status}
                              size="small"
                              inputProps={{
                                'aria-label': intl.formatMessage(
                                  {
                                    id: 'project.table.bankStatusFor',
                                    defaultMessage: '{bank} banka durumu',
                                  },
                                  { bank: item.bankTitle }
                                ),
                              }}
                              sx={{
                                height: 30,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: statusCfg.color,
                                bgcolor: statusCfg.backgroundColor,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: statusCfg.borderColor,
                                },
                              }}
                              onChange={(e) =>
                                handleStatusChange(
                                  item.companyCode,
                                  item.bankId,
                                  Number(e.target.value) as BOOK_BANK_STATUS_TYPE_ENUM
                                )
                              }>
                              {statusOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <BankStatusChip status={item.status} />
                        )}

                        {editable && (
                          <Tooltip title={<FormattedMessage id="project.table.delete" defaultMessage="Sil" />}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBank(item.companyCode, item.bankId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          <FormattedMessage
            id="project.table.limitStatus"
            defaultMessage="{current} / {limit} Pasif Olmayan Banka · {total} Toplam"
            values={{ current: bankLimitUsageCount, limit: bankCount, total: totalCurrentBanks }}
          />
        </Typography>

        <Box display="flex" gap={1}>
          {editable ? (
            <>
              <Button onClick={onClose} variant="outlined" color="inherit" size="small">
                <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
              </Button>
              <Button
                onClick={handleCommitSave}
                variant="contained"
                color="primary"
                size="small"
                disabled={Boolean(draftValidationIssue)}>
                <FormattedMessage id="dashboard.save" defaultMessage="Kaydet" />
              </Button>
            </>
          ) : (
            <Button onClick={onClose} variant="contained" size="small">
              <FormattedMessage id="dashboard.okButtonLabel" defaultMessage="Tamam" />
            </Button>
          )}
        </Box>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ProductBanksDialog;
